import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, User, Clock, Users } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/student/classes")({ component: MyClassesPage });

function MyClassesPage() {
  const { user } = useAuth();
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const classesQ = useQuery({
    queryKey: ["student-my-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: enrolls } = await supabase
        .from("enrollments").select("class_id").eq("student_id", user!.id).eq("is_active", true);
      const classIds = (enrolls ?? []).map(e => e.class_id);
      if (!classIds.length) return [];

      const { data: classes } = await supabase
        .from("classes")
        .select("id, name, subject, class_code, schedule_note, capacity, status, teacher_id")
        .in("id", classIds);

      const teacherIds = [...new Set((classes ?? []).map(c => c.teacher_id).filter((x): x is string => !!x))];
      const { data: teachers } = teacherIds.length
        ? await supabase.from("profiles").select("id, display_name, email").in("id", teacherIds)
        : { data: [] };
      const teacherMap = new Map((teachers ?? []).map(t => [t.id, t]));

      const { data: allEnrolls } = await supabase
        .from("enrollments").select("class_id").in("class_id", classIds).eq("is_active", true);
      const countMap = new Map<string, number>();
      (allEnrolls ?? []).forEach(e => countMap.set(e.class_id, (countMap.get(e.class_id) ?? 0) + 1));

      return (classes ?? []).map(c => ({
        ...c,
        teacher: c.teacher_id ? teacherMap.get(c.teacher_id) ?? null : null,
        studentCount: countMap.get(c.id) ?? 0,
      }));
    },
  });

  return (
    <PortalLayout portal="student" title="Lớp học của tôi"
      breadcrumb={[{ label: "Học sinh", to: "/student" }, { label: "Lớp học" }]}>

      {classesQ.isLoading ? (
        <div className="text-sm text-muted-foreground">Đang tải...</div>
      ) : (classesQ.data?.length ?? 0) === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="size-10 mx-auto mb-3 text-muted-foreground" />
          <div className="font-semibold">Chưa có lớp học nào</div>
          <div className="text-sm text-muted-foreground mt-1">Liên hệ trung tâm để được ghi danh.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {(classesQ.data ?? []).map((c, i) => {
            const isExpanded = expanded === c.id;
            return (
              <motion.div key={c.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                layout
                onClick={() => setExpanded(isExpanded ? null : c.id)}
                className="bg-card border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-semibold truncate">{c.name}</h3>
                    {c.subject && <p className="text-sm text-muted-foreground">{c.subject}</p>}
                  </div>
                  {c.class_code && (
                    <code className="text-[10px] px-2 py-1 bg-muted rounded font-mono shrink-0">{c.class_code}</code>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="size-4 shrink-0" />
                    <span className="truncate">{c.teacher?.display_name ?? "Chưa phân công"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="size-4 shrink-0" />
                    <span>{c.studentCount} học sinh</span>
                  </div>
                  {c.schedule_note && (
                    <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                      <Clock className="size-4 shrink-0" />
                      <span className="truncate">{c.schedule_note}</span>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 pt-4 border-t space-y-2 overflow-hidden"
                  >
                    {c.teacher?.email && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Email GV: </span>
                        <span className="font-medium">{c.teacher.email}</span>
                      </div>
                    )}
                    <div className="text-sm flex items-center gap-2">
                      <span className="text-muted-foreground">Trạng thái:</span>
                      <Badge className={
                        c.status === "active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" :
                        c.status === "paused" ? "bg-amber-100 text-amber-700 hover:bg-amber-100" :
                        "bg-slate-100 text-slate-500 hover:bg-slate-100"
                      }>
                        {c.status === "active" ? "Đang học" : c.status === "paused" ? "Tạm nghỉ" : "Đã kết thúc"}
                      </Badge>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Sức chứa: </span>
                      <span>{c.studentCount} / {c.capacity}</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
}
